import { ActionCreatorWithPayload, AnyAction, createListenerMiddleware, ListenerEffectAPI, ThunkDispatch } from "@reduxjs/toolkit";
import _ from "lodash";
import { TaxReduxDispatch, TaxReduxState } from "./reduxStore";
import { bulkSaveJsonPreferences, findPreferences } from "../../backend/taxguru/client/jmf/corePreferencesClient";
import { updateAccountTableState } from "../accountSlice";
import { TableStateType } from "@mm-products/java-multima-framework";
import { updateSchedulerTableState } from "../schedulerSlice";
import { updateDmsTableState } from "../dmsSlice";
import { updateUserRoleTableState } from "../userRolesSlice";
import { updateCompanyTableState } from "../companySlice";
import { updateClientTableState } from "../clientSlice";
import { updateLicenceTableState } from "../licenceSlice";
import { updatePersonTableState } from "../personSlice";
import { updateDocumentTableState } from "../documentSlice";
import { updateAddressTableState } from "../addressSlice";
import { updatePreAccountingTableState } from "../preAccountingSlice";
import { updateAuditTableState } from "../auditSlice";
import { updateContactTableState } from "../contactSlice";
import { updatePaymentRequestTableState } from "../paymentRequestSlice";
import { updatePaymentTransactionTableState } from "../paymentTransactionSlice";
import { updateTaskTableState } from "../taskSlice";
import { updateAccountantTableState } from "../accountantSlice";
import { updateDocumentItemsTableState } from "../documentItemsSlice";
import { TaxTableStateType } from "./commonTableSliceFeatures";
import { updatePaymentOrderTableState } from "../paymentOrderSlice";
import { updatePaymentOfferTableState } from "../paymentOfferSlice";
import { updateRegistrationRequestTableState } from "../registrationRequestSlice";
import { updateVatRatesTableState } from "../vatRatesSlice";
import { updatePaymentCouponTableState } from "../paymentCouponSlice";
import { updateCentreTableState } from "../centreSlice";
import { updateEmailTableState } from "../emailSlice";
import { updateActivityTableState } from "../activitySlice";
import { updateContractTableState } from "../contractSlice";
import { updateOfficerTableState } from "../officerSlice";
import { updateApproverTableState } from "../approverSlice";

const tableKeyMaker = (key: string) => key + "-TABLE";
const keyMappings: Array<{ reducerName: string; key: string; dispatchState: ActionCreatorWithPayload<TableStateType> }> = [
    { reducerName: "account", key: tableKeyMaker("ACCOUNT"), dispatchState: updateAccountTableState },
    { reducerName: "scheduler", key: tableKeyMaker("SCHEDULER"), dispatchState: updateSchedulerTableState },
    { reducerName: "dms", key: tableKeyMaker("DMS"), dispatchState: updateDmsTableState },
    { reducerName: "userRoles", key: tableKeyMaker("USER-ROLES"), dispatchState: updateUserRoleTableState },
    { reducerName: "company", key: tableKeyMaker("COMPANY"), dispatchState: updateCompanyTableState },
    { reducerName: "client", key: tableKeyMaker("CLIENT"), dispatchState: updateClientTableState },
    { reducerName: "licence", key: tableKeyMaker("LICENCE"), dispatchState: updateLicenceTableState },
    { reducerName: "accountant", key: tableKeyMaker("ACCOUNTANT"), dispatchState: updateAccountantTableState },
    { reducerName: "person", key: tableKeyMaker("PERSON"), dispatchState: updatePersonTableState },
    { reducerName: "document", key: tableKeyMaker("DOCUMENT"), dispatchState: updateDocumentTableState },
    { reducerName: "documentItems", key: tableKeyMaker("DOCUMENT-ITEMS"), dispatchState: updateDocumentItemsTableState },
    { reducerName: "address", key: tableKeyMaker("ADDRESS"), dispatchState: updateAddressTableState },
    { reducerName: "preAccounting", key: tableKeyMaker("PRE-ACCOUTING"), dispatchState: updatePreAccountingTableState },
    { reducerName: "centre", key: tableKeyMaker("CENTRE"), dispatchState: updateCentreTableState },
    { reducerName: "audit", key: tableKeyMaker("AUDIT"), dispatchState: updateAuditTableState },
    { reducerName: "vatRates", key: tableKeyMaker("VAT-RATES"), dispatchState: updateVatRatesTableState },
    { reducerName: "contact", key: tableKeyMaker("CONTACT"), dispatchState: updateContactTableState },
    { reducerName: "paymentOrder", key: tableKeyMaker("PAYMENT-ORDER"), dispatchState: updatePaymentOrderTableState },
    { reducerName: "paymentOffer", key: tableKeyMaker("PAYMENT-OFFER"), dispatchState: updatePaymentOfferTableState },
    { reducerName: "paymentCoupon", key: tableKeyMaker("PAYMENT-COUPON"), dispatchState: updatePaymentCouponTableState },
    { reducerName: "paymentRequest", key: tableKeyMaker("PAYMENT-REQUEST"), dispatchState: updatePaymentRequestTableState },
    { reducerName: "paymentTransaction", key: tableKeyMaker("PAYMENT-TRANSACTION"), dispatchState: updatePaymentTransactionTableState },
    { reducerName: "task", key: tableKeyMaker("TASK"), dispatchState: updateTaskTableState },
    { reducerName: "registrationRequest", key: tableKeyMaker("REGISTRATION-REQUEST"), dispatchState: updateRegistrationRequestTableState },
    { reducerName: "email", key: tableKeyMaker("EMAIL"), dispatchState: updateEmailTableState },
    { reducerName: "activity", key: tableKeyMaker("ACTIVITY"), dispatchState: updateActivityTableState },
    { reducerName: "contract", key: tableKeyMaker("CONTRACT"), dispatchState: updateContractTableState },
    { reducerName: "officer", key: tableKeyMaker("OFFICER"), dispatchState: updateOfficerTableState },
    { reducerName: "approver", key: tableKeyMaker("APPROVER"), dispatchState: updateApproverTableState },
];

export const tableStateUpdatesReduxListenerInstance = createListenerMiddleware();
const actionNameRegex = /update(\w+)TableState/g;

const persistTableStates = _.debounce(async function (api: ListenerEffectAPI<unknown, ThunkDispatch<unknown, unknown, AnyAction>>) {
    const state: TaxReduxState = api.getState() as TaxReduxState;
    if (!state.user.userId) {
        console.warn("Empty user ID when persisting table states");
        return;
    }
    const dataReadyToSave: Array<[string, TaxTableStateType]> = Object.entries(state)
        .filter((entry) => entry[1]?.tableState !== undefined)
        .map((entry) => {
            const foundMapping = keyMappings.find((val) => val.reducerName === entry[0]);
            if (foundMapping) {
                return { entry, foundMapping };
            }
            return undefined;
        })
        .filter((val) => val !== undefined)
        .map((val) => {
            const tableState = { ...val!.entry[1].tableState! };
            delete tableState["resetIndicator"];
            return [val!.foundMapping.key, tableState];
        });
    await bulkSaveJsonPreferences(dataReadyToSave, state.user.userId);
}, 2000);

tableStateUpdatesReduxListenerInstance.startListening({
    predicate: (action) => actionNameRegex.test(action.type),
    effect: (action, api) => persistTableStates(api),
});

export function initialiseTableStatesThunk(userId: string) {
    return async (dispatch: TaxReduxDispatch) => {
        const response = await findPreferences(
            keyMappings.map((val) => val.key),
            userId
        );
        if (!response.data.data || response.data.data.length === 0) {
            return;
        }
        const { data } = response.data;
        const preferencesByKey = _.groupBy(data, (value) => value.prefKey?.toUpperCase());
        for (const keyMapping of keyMappings) {
            const preferencesByKeyElement = preferencesByKey[keyMapping.key];
            if (preferencesByKeyElement?.[0]?.jsonValue) {
                try {
                    const jsonValue = JSON.parse(preferencesByKeyElement[0].jsonValue);
                    dispatch(keyMapping.dispatchState(jsonValue));
                } catch (err) {
                    console.error("Error initialising table state", keyMapping.key, err);
                }
            }
        }
    };
}
